import type { Milliseconds } from "../units.js";
import { getTimeMs } from "../utils.js";
import type { BaseCache } from "./BaseCache.js";
import { areDependenciesEqual } from "../areDependenciesEqual.js";


class LRUCacheEntry<V> {
    readonly dependencies: any[];
    readonly value: V;
    lastUseTime: Milliseconds;

    constructor(dependencies: any[], value: V, time: Milliseconds) {
        this.dependencies = dependencies;
        this.value = value;
        this.lastUseTime = time;
    }

    markUse(time: Milliseconds): void {
        this.lastUseTime = time;
    }

    areDependenciesEqual(deps: any[]): boolean {
        return areDependenciesEqual(this.dependencies, deps);
    }
}


/**
 * Least-Recently-Used cache for expensive computations.
 */
export class LRUCache<V> implements BaseCache<any[], V> {
    private readonly name: string;
    private readonly capacity: number;
    private readonly entries: LRUCacheEntry<V>[] = [];
    private readonly getTime: () => Milliseconds;

    cacheHits = 0;
    cacheMisses = 0;

    constructor(name: string, capacity: number, getTimeMsFn?: () => Milliseconds) {
        this.name = name;
        this.capacity = capacity;
        this.getTime = getTimeMsFn ?? getTimeMs;
    }

    private checkCacheHealth(): void {
        const total = this.cacheHits + this.cacheMisses;
        if (total > 0 && this.cacheMisses / total > 0.5) {
            console.warn(
                `High cache miss ratio for ${this.name} – hits: ${this.cacheHits}, misses: ${this.cacheMisses}.`,
            );
        }
        this.cacheHits = 0;
        this.cacheMisses = 0;
    }

    private findEntry(deps: any[]): LRUCacheEntry<V> | null {
        for (const e of this.entries) {
            if (e.areDependenciesEqual(deps))
                return e;
        }
        return null;
    }

    private discardOldest(): void {
        const now = this.getTime();
        let oldestIdx: number | null = null;
        let oldestAge: Milliseconds | null = null;

        for (let i = 0; i < this.entries.length; ++i) {
            const age = now - this.entries[i].lastUseTime;
            if (oldestAge === null || age > oldestAge) {
                oldestAge = age;
                oldestIdx = i;
            }
        }
        if (oldestIdx !== null) {
            this.entries.splice(oldestIdx, 1);
        }
    }

    getOrCompute(deps: any[], compute: () => V): V {
        try {
            const hit = this.findEntry(deps);
            if (hit) {
                hit.markUse(this.getTime());
                ++this.cacheHits;
                return hit.value;
            }

            const value = compute();
            ++this.cacheMisses;

            if (this.entries.length >= this.capacity) {
                this.discardOldest();
            }
            this.entries.push(new LRUCacheEntry<V>(deps, value, this.getTime()));
            return value;
        } finally {
            if (this.cacheHits + this.cacheMisses >= 1_000) {
                this.checkCacheHealth();
            }
        }
    }

    async getOrComputeAsync(deps: any[], compute: () => Promise<V>): Promise<V> {
        try {
            const hit = this.findEntry(deps);
            if (hit) {
                hit.markUse(this.getTime());
                ++this.cacheHits;
                return hit.value;
            }

            const value = await compute();
            ++this.cacheMisses;

            if (this.entries.length >= this.capacity) {
                this.discardOldest();
            }
            this.entries.push(new LRUCacheEntry<V>(deps, value, this.getTime()));
            return value;
        } finally {
            if (this.cacheHits + this.cacheMisses >= 1_000) {
                this.checkCacheHealth();
            }
        }
    }

    clear(): void {
        this.entries.length = 0;
        this.cacheHits = 0;
        this.cacheMisses = 0;
    }

    get size(): number {
        return this.entries.length;
    }
}
